import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { errorText } from '../../lib/api';
import { characterName, useRunScope } from '../../lib/characters';
import {
  itemKey,
  playerProgress,
  progressQueryKey,
  resetProgress,
  STATE_LABELS,
  type ProgressItem,
  type ProgressResetResponse,
  type ProgressResetSelection,
} from '../../lib/progressApi';

/**
 * The Reset progress dialog (card #251): what the player has cleared or progressed, grouped (story, bosses, missions, scan
 * triggers, world flags, key-part hunts, tech), with a group filter, per-item checkboxes, "select all in group",
 * "Reset everything shown", a preview of exactly what goes, a required reason, then the server's summary of what was reset.
 */
export function ResetProgressDialog({
  playerId,
  onClose,
}: {
  playerId: string;
  onClose: () => void;
}) {
  const { client, runId, character } = useRunScope();
  const queryClient = useQueryClient();
  const progress = useQuery({
    queryKey: progressQueryKey(playerId, runId),
    queryFn: () => playerProgress(client, playerId),
  });
  const [group, setGroup] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [everything, setEverything] = useState(false);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<ProgressResetResponse | null>(null);

  const groups = useMemo(() => progress.data?.groups ?? [], [progress.data]);
  const shown = group ? groups.filter((g) => g.group === group) : groups;
  const shownItems = shown.flatMap((g) => g.items);
  const allItems = groups.flatMap((g) => g.items);
  const preview: ProgressItem[] = everything
    ? shownItems
    : allItems.filter((i) => selected.has(itemKey(i)));
  const accountItems = preview.filter((i) => i.scope === 'account').length;

  const reset = useMutation({
    mutationFn: () => {
      const selection: ProgressResetSelection = everything
        ? { all: true, groups: group ? [group] : undefined }
        : { all: false, items: preview.map((i) => ({ kind: i.kind, id: i.id })) };
      return resetProgress(client, playerId, selection, reason.trim(), runId);
    },
    onSuccess: (r) => {
      setResult(r);
      setSelected(new Set());
      setEverything(false);
      queryClient.setQueryData(progressQueryKey(playerId, runId), r.progress);
    },
  });

  const toggle = (key: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleGroup = (keys: string[], on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  const groupTitle = groups.find((g) => g.group === group)?.title;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-progress-title"
        className="w-full max-w-3xl rounded border-2 border-ink bg-white p-4 shadow-lg"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 id="reset-progress-title" className="text-lg font-semibold">
            Reset progress
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded border-2 border-ink px-2 py-0.5 text-sm"
          >
            Close
          </button>
        </div>
        <p className="mt-1 text-sm text-bruise">
          Pick what to replay. Account-wide progress (story, trophies, blueprints, mastery, account
          flags) is reset here on purpose, even though it survives a run death. Run progress is{' '}
          {characterName(character)}&apos;s only. Nothing is granted, taken or moved: inventory and
          loadouts stay as they are.
        </p>

        {progress.isPending && <p className="mt-3">Loading&hellip;</p>}
        {progress.isError && <p className="mt-3 text-blam">{errorText(progress.error)}</p>}

        {progress.data && (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-1">
                Group
                <select
                  aria-label="Group"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  className="rounded border-2 border-ink px-2 py-1"
                >
                  <option value="">All groups ({allItems.length})</option>
                  {groups.map((g) => (
                    <option key={g.group} value={g.group}>
                      {g.title} ({g.items.length})
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={everything}
                  onChange={(e) => setEverything(e.target.checked)}
                  disabled={shownItems.length === 0}
                />
                {group ? `Reset all ${groupTitle ?? group}` : 'Reset everything'}
              </label>
            </div>

            <div className="mt-3 max-h-[45vh] space-y-3 overflow-y-auto">
              {shown.map((g) => {
                const keys = g.items.map(itemKey);
                const allOn = keys.length > 0 && keys.every((k) => selected.has(k));
                return (
                  <section
                    key={g.group}
                    aria-label={g.title}
                    className="rounded border border-ink/30 p-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold">{g.title}</h3>
                      {g.items.length > 0 && (
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            aria-label={`Select all in ${g.title}`}
                            checked={everything || allOn}
                            disabled={everything}
                            onChange={(e) => toggleGroup(keys, e.target.checked)}
                          />
                          Select all in group
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-bruise">{g.note}</p>
                    {g.items.length === 0 ? (
                      <p className="mt-1 text-xs italic">Nothing here.</p>
                    ) : (
                      <ul className="mt-1 space-y-1">
                        {g.items.map((i) => {
                          const key = itemKey(i);
                          return (
                            <li key={key} className="flex items-start gap-2 text-sm">
                              <input
                                type="checkbox"
                                aria-label={i.title}
                                checked={everything || selected.has(key)}
                                disabled={everything}
                                onChange={() => toggle(key)}
                                className="mt-1"
                              />
                              <div>
                                <span className="font-medium">{i.title}</span>{' '}
                                <span className="rounded bg-caption px-1 text-xs">
                                  {STATE_LABELS[i.state] ?? i.state}
                                </span>{' '}
                                <span className="text-xs text-bruise">
                                  {i.scope === 'account' ? 'account' : characterName(character)}
                                  {i.at ? ` · ${new Date(i.at).toLocaleString()}` : ''}
                                </span>
                                {i.detail && <div className="text-xs">{i.detail}</div>}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>

            <section
              aria-label="Preview"
              className="mt-3 rounded border-2 border-ink/60 bg-newsprint p-2 text-sm"
            >
              <h3 className="font-semibold">
                {preview.length === 0
                  ? 'Nothing selected'
                  : `This will reset ${preview.length} item${preview.length === 1 ? '' : 's'}`}
                {accountItems > 0 && ` (${accountItems} account-wide)`}
              </h3>
              <ul className="mt-1 list-disc pl-5 text-xs">
                {preview.map((i) => (
                  <li key={itemKey(i)}>
                    <span className="font-medium">{i.title}</span>: {i.removes}
                  </li>
                ))}
              </ul>
            </section>

            <form
              className="mt-3 flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                reset.mutate();
              }}
            >
              <input
                aria-label="Reason"
                placeholder="reason (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-w-0 flex-1 rounded border-2 border-ink px-2 py-1"
              />
              <button
                type="submit"
                disabled={preview.length === 0 || !reason.trim() || reset.isPending}
                className="rounded border-2 border-ink bg-blam px-3 py-1 font-semibold text-white disabled:opacity-50"
              >
                {reset.isPending
                  ? 'Resetting…'
                  : `Reset ${preview.length} item${preview.length === 1 ? '' : 's'}`}
              </button>
            </form>
            {reset.isError && <p className="mt-2 text-sm text-blam">{errorText(reset.error)}</p>}
          </>
        )}

        {result && (
          <section
            aria-label="Result"
            className="mt-3 rounded border-2 border-ink bg-white p-2 text-sm"
          >
            <h3 className="font-semibold">
              Reset {result.reset.length} item{result.reset.length === 1 ? '' : 's'}
              {result.skipped.length > 0 && `, ${result.skipped.length} already clear`}
            </h3>
            <ul className="mt-1 list-disc pl-5 text-xs">
              {result.reset.map((r) => (
                <li key={itemKey(r)}>
                  <span className="font-medium">{r.title}</span>: {r.removed}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
